import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Paper,
  Collapse,
  Button,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GlassCard from "../../../components/common/GlassCard";

function SocialHistoryCard({ history, onAddSocialHistory, onDeleteSocialHistory }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [expandAll, setExpandAll] = useState(false);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveId(null);
  };

  const sortedHistory = history
    ? [...history].sort((a, b) => {
        const dateA = new Date(a.created_at || a.id || 0);
        const dateB = new Date(b.created_at || b.id || 0);
        return dateB - dateA;
      })
    : [];

  return (
    <GlassCard
      title="Social History"
      addLabel="Add Social History"
      icon={<WorkIcon color="info" />}
      onAdd={onAddSocialHistory}
    >
      {history?.length ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {history.slice(0, 3).map((item) => (
              <Paper
                key={item.id}
                sx={{
                  p: 1.25,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.75}>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600} mb={0.5}>
                      {item.category || "Social"}{item.created_at ? ` • ${new Date(item.created_at).toLocaleDateString()}` : ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.status}
                    </Typography>
                    {item.details && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        {item.details}
                      </Typography>
                    )}
                  </Box>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, item.id)}
                      sx={{ ml: 0.5 }}
                      aria-label="Social history options menu"
                    >
                      <MoreVertIcon aria-label="Social history options menu" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            ))}
          </Box>

          {sortedHistory?.length > 2 && (
            <>
              <Collapse in={expandAll} timeout="auto" unmountOnExit>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                  {history.slice(3).map((item) => (
                    <Paper
                      key={item.id}
                      sx={{
                        p: 1.25,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.75}>
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={600} mb={0.5}>
                            {item.category || "Social"}{item.created_at ? ` • ${new Date(item.created_at).toLocaleDateString()}` : ""}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {item.status}
                          </Typography>
                          {item.details && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                              {item.details}
                            </Typography>
                          )}
                        </Box>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, item.id)}
                            sx={{ ml: 0.5 }}
                            aria-label="Social history options menu"
                          >
                            <MoreVertIcon aria-label="Social history options menu" fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Collapse>

              <Button
                size="small"
                onClick={() => setExpandAll(!expandAll)}
                startIcon={expandAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: "none", mt: 1 }}
              >
                {expandAll ? "View Less" : `View All (${sortedHistory.length - 2} more)`}
              </Button>
            </>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No social history
        </Typography>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (activeId) {
              onDeleteSocialHistory(activeId);
            }
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

SocialHistoryCard.propTypes = {
  history: PropTypes.array,
  onAddSocialHistory: PropTypes.func.isRequired,
  onDeleteSocialHistory: PropTypes.func.isRequired,
};

SocialHistoryCard.defaultProps = {
  history: [],
};

export default SocialHistoryCard;
